import React, { useReducer, useEffect, useMemo, useCallback } from "react";
import { listenLocations } from '../services/branchService';
import { POS_CONFIG_APP_ID } from '@config/constants';
import { STORAGE_KEY_POS, STORAGE_KEY_MERCHANT, STORAGE_KEY_LAST_USED } from "../utils/storageKeys";
// We import context and helpers from LocationContextCore to satisfy Fast Refresh
// which requires this file to only export components.
import { LocationContext, getActiveLocations } from "./LocationContextCore";

const initialState = {
  effectiveUid: null,
  locations: [],
  loadingLocations: true,
  lastSelectedId: localStorage.getItem(STORAGE_KEY_POS) || null,
};

function locationReducer(state, action) {
  switch (action.type) {
    case 'SET_EFFECTIVE_UID':
      if (state.effectiveUid === action.payload) return state;
      return { ...state, effectiveUid: action.payload };
    case 'SET_LOCATIONS':
      return { ...state, locations: action.payload, loadingLocations: false };
    case 'SET_LOADING':
      if (state.loadingLocations === action.payload) return state;
      return { ...state, loadingLocations: action.payload };
    case 'SET_LAST_SELECTED':
      return { ...state, lastSelectedId: action.payload };
    case 'RESET_AUTH':
      return { ...state, effectiveUid: null, locations: [], loadingLocations: false };
    default:
      return state;
  }
}

export const LocationProvider = ({ children, user, ownerIdForLocations }) => {
  const [state, dispatch] = useReducer(locationReducer, initialState);
  const { effectiveUid, locations, loadingLocations, lastSelectedId } = state;

  // 1. Resolve Effective UID (Merchant ID for Staff, UID for Owner)
  useEffect(() => {
    // 1a. If owner ID is provided directly, use it
    if (ownerIdForLocations) {
      dispatch({ type: 'SET_EFFECTIVE_UID', payload: ownerIdForLocations });
      return;
    }

    // 1b. If no user, reset states
    if (!user) {
      dispatch({ type: 'RESET_AUTH' });
      return;
    }

    // 1c. Async resolution of merchantId
    let cancelled = false;
    user.getIdTokenResult().then((token) => {
      if (cancelled) return;
      const resolvedUid = token?.claims?.merchantId || user.uid;
      dispatch({ type: 'SET_EFFECTIVE_UID', payload: resolvedUid });
    }).catch((err) => {
      if (cancelled) return;
      console.warn("[LocationContext] Failed to get token result, falling back to user.uid:", err);
      dispatch({ type: 'SET_EFFECTIVE_UID', payload: user.uid });
    });

    return () => { cancelled = true; };
  }, [user, ownerIdForLocations]);

  // 2. Firestore Listener
  useEffect(() => {
    if (!effectiveUid) return;
    
    dispatch({ type: 'SET_LOADING', payload: true });
    let unsub = listenLocations(POS_CONFIG_APP_ID, effectiveUid, (list) => {
      dispatch({ type: 'SET_LOCATIONS', payload: list });
      
      // Auto-select if nothing selected
      const savedId = localStorage.getItem(STORAGE_KEY_POS);
      const activeList = getActiveLocations(list);
      
      if (!savedId && activeList.length > 0) {
        const defaultId = activeList[0].id;
        localStorage.setItem(STORAGE_KEY_POS, defaultId);
        dispatch({ type: 'SET_LAST_SELECTED', payload: defaultId });
      } else if (list.length === 1 && !savedId) {
        const singleId = list[0].id;
        localStorage.setItem(STORAGE_KEY_POS, singleId);
        dispatch({ type: 'SET_LAST_SELECTED', payload: singleId });
      }
    });
    
    return () => unsub();
  }, [effectiveUid]);

  // Derived locations
  const currentLocations = useMemo(() => (effectiveUid ? locations : []), [effectiveUid, locations]);
  const activeLocations = useMemo(() => getActiveLocations(currentLocations), [currentLocations]);

  // Handle active location persistence
  const setSelectedLocationId = useCallback((id) => {
    if (id) {
      localStorage.setItem(STORAGE_KEY_POS, id);
      localStorage.setItem(STORAGE_KEY_LAST_USED, new Date().toISOString());
      dispatch({ type: 'SET_LAST_SELECTED', payload: id });
    } else {
      localStorage.removeItem(STORAGE_KEY_POS);
      dispatch({ type: 'SET_LAST_SELECTED', payload: null });
    }
  }, []);

  const activeLocation = useMemo(() => {
    if (!lastSelectedId || currentLocations.length === 0) return null;
    return currentLocations.find(loc => loc.id === lastSelectedId) || null;
  }, [lastSelectedId, currentLocations]);

  // API alignment for features like InventoryManager
  const value = {
    locations: currentLocations,
    activeLocations, // Important for UI dropdowns
    selectedLocationId: lastSelectedId, // Important for data loading
    setSelectedLocationId, // Mapping to legacy name
    loadingLocations, // Important for loading states
    activeLocation,
    merchantId: effectiveUid
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
